import { Dispatch, MouseEvent, ReactNode, SetStateAction } from 'react';
import { useIntl } from 'react-intl';
import { SchemaDatapointEnumOption } from '@rossum/api-client/schemas';
import { Box, SxProps, Theme, Typography } from '@rossum/ui/material';
import {
  GridBooleanCell,
  GridRenderCellParams,
} from '@rossum/ui/x-data-grid-pro';
import { text } from '../../../constants';
import { GridRowModel } from '../../rows/rowTypes';
import { EnumAndFormulaCellValue } from '../types';
import { ChipsCell } from './ChipsCell';
import { NotEditableDash } from './NotEditableDash';

export const renderBooleanCell = (params: GridRenderCellParams<GridRowModel>) =>
  params.isEditable ? <GridBooleanCell {...params} /> : text.cellNotEditable;

// our custom valueGetter is sending value that isn't in the options
// params.formattedValue resolves to empty string and params.value to undefined
// this allows the content for empty cell from dataGridStyles.ts to be used
export const renderSingleSelectCell = (
  params: GridRenderCellParams<GridRowModel>
) => (
  <div className="singleSelectCellContent">
    {(params.formattedValue || params.value) ??
      (params.isEditable ? undefined : text.cellNotEditable)}
  </div>
);

export const renderCellWithCount =
  (
    setOptionsForEnumDialog?: Dispatch<
      SetStateAction<{
        options: SchemaDatapointEnumOption[];
        value: EnumAndFormulaCellValue;
      } | null>
    >
  ) =>
  (params: GridRenderCellParams<GridRowModel>) => {
    const intl = useIntl();
    return (
      <Typography
        component="span"
        sx={{
          cursor: 'pointer',
        }}
        alignItems="center"
        color="secondary"
        onClick={e => {
          e.stopPropagation();

          if ('options' in params.row && setOptionsForEnumDialog) {
            setOptionsForEnumDialog({
              options: params.row.options,
              value: params.value,
            });
          }
        }}
      >
        {params.value?.label ? (
          <>
            <Typography component="span" variant="body2">
              {params.value.label}
            </Typography>
            <Typography color="text.secondary" component="span" variant="body2">
              {intl.formatMessage(
                {
                  id: 'features.fieldManager.fieldDetail.columns.renderCell.textSecondary',
                },
                { count: params.value.count }
              )}
            </Typography>
          </>
        ) : params.isEditable ? undefined : (
          <NotEditableDash />
        )}
      </Typography>
    );
  };

export const renderFormulaCellWithCount =
  (
    setFormulaForFormulaDrawer?: Dispatch<
      SetStateAction<{
        formula: string;
        value: EnumAndFormulaCellValue;
      } | null>
    >
  ) =>
  (params: GridRenderCellParams<GridRowModel>) => (
    <Typography
      component="span"
      alignItems="center"
      color="secondary"
      sx={{
        cursor: 'pointer',
      }}
      onClick={e => {
        e.stopPropagation();

        if (
          'formula' in params.row &&
          params.row.formula &&
          setFormulaForFormulaDrawer
        ) {
          setFormulaForFormulaDrawer({
            formula: params.row.formula,
            value: {
              label: params.value.label,
              count: params.value.count,
            },
          });
        }
      }}
    >
      {params.value?.label ? (
        <>
          <Typography component="span" variant="body2">
            {params.value.label}
          </Typography>
          <Typography color="text.secondary" component="span" variant="body2">
            {` (${params.value.count} characters)`}
          </Typography>
        </>
      ) : params.isEditable ? undefined : (
        <NotEditableDash />
      )}
    </Typography>
  );

export const renderLookupCellWithCount =
  (
    setLookupForDrawer?: Dispatch<
      SetStateAction<{
        matching: Record<string, unknown>;
        value: EnumAndFormulaCellValue;
      } | null>
    >
  ) =>
  (params: GridRenderCellParams<GridRowModel>) => (
    <Typography
      component="span"
      alignItems="center"
      color="secondary"
      sx={{
        cursor: 'pointer',
      }}
      onClick={e => {
        e.stopPropagation();

        if (
          'matching' in params.row &&
          params.row.matching &&
          setLookupForDrawer
        ) {
          setLookupForDrawer({
            matching: params.row.matching,
            value: {
              label: params.value.label,
              count: params.value.count,
            },
          });
        }
      }}
    >
      {params.value?.label ? (
        <>
          <Typography component="span" variant="body2">
            {params.value.label}
          </Typography>
          <Typography color="text.secondary" component="span" variant="body2">
            {` (${params.value.count} characters)`}
          </Typography>
        </>
      ) : params.isEditable ? undefined : (
        <NotEditableDash />
      )}
    </Typography>
  );

type ReasoningDrawerPayload = {
  prompt: string;
  context: string[];
  value: EnumAndFormulaCellValue;
};

type ReasoningSetter = Dispatch<SetStateAction<ReasoningDrawerPayload | null>>;

const getReasoningDrawerPayload = (
  row: GridRowModel,
  cellValue?: EnumAndFormulaCellValue
): ReasoningDrawerPayload | null => {
  if (!('prompt' in row) || typeof row.prompt !== 'string') {
    return null;
  }

  const promptText = row.prompt;
  const context =
    'context' in row && Array.isArray(row.context) ? row.context : [];

  if (cellValue?.label !== undefined) {
    return { prompt: promptText, context, value: cellValue };
  }

  const { promptHashMap } = (
    row.meta as { metadata: { promptHashMap?: Record<string, string> } }
  ).metadata;
  const count = promptText.length;
  const label =
    count === 0 ? 'Empty prompt' : (promptHashMap?.[promptText] ?? promptText);

  return { prompt: promptText, context, value: { label, count } };
};

type ReasoningClickableProps = {
  payload: ReasoningDrawerPayload | null;
  setReasoningForDrawer?: ReasoningSetter;
  component?: 'span' | 'div';
  sx?: SxProps<Theme>;
  children: ReactNode;
};

const ReasoningClickable = ({
  payload,
  setReasoningForDrawer,
  component = 'span',
  sx,
  children,
}: ReasoningClickableProps) => {
  const clickable = !!payload && !!setReasoningForDrawer;

  return (
    <Box
      component={component}
      onClick={
        clickable
          ? (e: MouseEvent) => {
              e.stopPropagation();
              setReasoningForDrawer(payload);
            }
          : undefined
      }
      sx={[
        clickable ? { cursor: 'pointer' } : null,
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {children}
    </Box>
  );
};

export const renderReasoningContextCell =
  (setReasoningForDrawer?: ReasoningSetter) =>
  (params: GridRenderCellParams<GridRowModel, Array<string> | string>) => {
    const hasContext =
      'context' in params.row &&
      Array.isArray(params.row.context) &&
      params.row.context.length > 0;

    const payload = hasContext ? getReasoningDrawerPayload(params.row) : null;

    return (
      <ReasoningClickable
        payload={payload}
        setReasoningForDrawer={setReasoningForDrawer}
        component="div"
        sx={{
          width: 1,
          height: 1,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <ChipsCell {...params} />
      </ReasoningClickable>
    );
  };

export const renderReasoningPromptCell =
  (setReasoningForDrawer?: ReasoningSetter) =>
  (params: GridRenderCellParams<GridRowModel>) => {
    const payload = getReasoningDrawerPayload(params.row, params.value);

    return (
      <ReasoningClickable
        payload={payload}
        setReasoningForDrawer={setReasoningForDrawer}
        sx={{ color: 'secondary.main', alignItems: 'center' }}
      >
        {params.value?.label ? (
          <>
            <Typography component="span" variant="body2">
              {params.value.label}
            </Typography>
            <Typography color="text.secondary" component="span" variant="body2">
              {` (${params.value.count} characters)`}
            </Typography>
          </>
        ) : params.isEditable ? undefined : (
          <NotEditableDash />
        )}
      </ReasoningClickable>
    );
  };
